/**
 * PhotoService — Abstraction over photo CRUD operations (currently Supabase).
 * When migrating storage, update storageService; DB ops stay here.
 */
import { supabase } from "@/integrations/supabase/client";
import { loadFeatureFlags } from "@/lib/featureFlags";
import { mediaService } from "./mediaService";
import { trackPhotoUpload, trackVideoUpload } from "@/lib/analytics";

export interface PhotoRecord {
  id: string;
  file_url: string;
  file_path: string;
  created_at: string;
  uploaded_by: string | null;
  media_type?: "image" | "video";
  thumbnail_url?: string | null;
  duration_seconds?: number | null;
  original_file_path?: string | null;
  storage_provider?: string | null;
  storage_key?: string | null;
  storage_url_mode?: "public";
  google_drive_file_id?: string | null;
  caption?: string | null;
}

export interface PhotoRecordFull extends PhotoRecord {
  approved: boolean;
  event_id: string;
}

const resolveMediaType = (mimeType: string): "image" | "video" => (
  mimeType.startsWith("video/") ? "video" : "image"
);

const getVideoDuration = (file: File): Promise<number | null> => new Promise((resolve) => {
  if (typeof document === "undefined") {
    resolve(null);
    return;
  }

  const video = document.createElement("video");
  const url = URL.createObjectURL(file);
  video.preload = "metadata";
  video.src = url;

  video.onloadedmetadata = () => {
    resolve(Number.isFinite(video.duration) ? Math.round(video.duration) : null);
    URL.revokeObjectURL(url);
  };

  video.onerror = () => {
    resolve(null);
    URL.revokeObjectURL(url);
  };
});

const computeFileHash = async (file: File): Promise<string> => {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const hashBuffer = await crypto.subtle.digest("SHA-256", bytes);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
};

const DEFAULT_IMAGE_UPLOAD_COMPRESSION_MIN_BYTES = 3 * 1024 * 1024;
const IMAGE_UPLOAD_COMPRESSION_MAX_LONG_EDGE = 2560;
const DEFAULT_IMAGE_UPLOAD_COMPRESSION_QUALITY = 0.88;
const IMAGE_THUMBNAIL_VARIANT_WIDTHS = [320, 480, 800, 1200] as const;
const DEFAULT_THUMBNAIL_VARIANT_WIDTH = 800;

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> => new Promise((resolve) => {
  canvas.toBlob((blob) => resolve(blob || null), type, quality);
});

const createVideoThumbnail = (file: File): Promise<Blob | null> => new Promise((resolve) => {
  if (typeof document === "undefined") {
    resolve(null);
    return;
  }

  const video = document.createElement("video");
  const url = URL.createObjectURL(file);
  video.preload = "metadata";
  video.muted = true;
  video.src = url;

  let settled = false;
  const finish = (blob: Blob | null) => {
    if (settled) return;
    settled = true;
    resolve(blob);
    URL.revokeObjectURL(url);
  };

  const captureFrame = () => {
    const origW = video.videoWidth || 720;
    const origH = video.videoHeight || 1280;

    // Proportionally resize to max 600px width
    const maxWidth = 600;
    const scale = Math.min(1, maxWidth / origW);
    const width = Math.round(origW * scale);
    const height = Math.round(origH * scale);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      finish(null);
      return;
    }

    ctx.drawImage(video, 0, 0, width, height);
    canvas.toBlob((blob) => finish(blob || null), "image/jpeg", 0.75);
  };

  video.onloadedmetadata = () => {
    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    const targetSecond = duration > 0 ? Math.min(1.2, Math.max(0.3, duration * 0.15)) : 0.5;

    if (duration > targetSecond) {
      video.currentTime = targetSecond;
      return;
    }

    captureFrame();
  };

  video.onseeked = captureFrame;

  video.onerror = () => {
    finish(null);
  };
});

const createImageThumbnail = (file: File, maxWidth = 600): Promise<Blob | null> => new Promise((resolve) => {
  if (typeof document === "undefined") {
    resolve(null);
    return;
  }

  const img = new Image();
  const url = URL.createObjectURL(file);

  img.onload = () => {
    const longEdge = Math.max(img.naturalWidth, img.naturalHeight);
    const scale = Math.min(1, maxWidth / longEdge);
    const width = Math.max(1, Math.round(img.naturalWidth * scale));
    const height = Math.max(1, Math.round(img.naturalHeight * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      URL.revokeObjectURL(url);
      resolve(null);
      return;
    }

    ctx.drawImage(img, 0, 0, width, height);
    canvas.toBlob((blob) => {
      URL.revokeObjectURL(url);
      resolve(blob || null);
    }, "image/jpeg", 0.75);
  };

  img.onerror = () => {
    URL.revokeObjectURL(url);
    resolve(null);
  };

  img.src = url;
});

const createImageThumbnailVariants = async (file: File): Promise<Array<{ width: number; blob: Blob }>> => {
  const variants: Array<{ width: number; blob: Blob }> = [];

  for (const width of IMAGE_THUMBNAIL_VARIANT_WIDTHS) {
    const blob = await createImageThumbnail(file, width);
    if (!blob) {
      throw new Error(`Failed to generate image thumbnail variant for width ${width}`);
    }
    variants.push({ width, blob });
  }

  return variants;
};

const compressImageForUpload = async (file: File, quality = DEFAULT_IMAGE_UPLOAD_COMPRESSION_QUALITY, minBytes = DEFAULT_IMAGE_UPLOAD_COMPRESSION_MIN_BYTES): Promise<File | null> => {
  if (typeof document === "undefined" || !file.type.startsWith("image/") || file.size <= minBytes) {
    return null;
  }

  const img = new Image();
  const url = URL.createObjectURL(file);

  const dimensions = await new Promise<{ width: number; height: number } | null>((resolve) => {
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = url;
  });

  URL.revokeObjectURL(url);
  if (!dimensions) return null;

  const longEdge = Math.max(dimensions.width, dimensions.height);
  const scale = Math.min(1, IMAGE_UPLOAD_COMPRESSION_MAX_LONG_EDGE / longEdge);
  const width = Math.max(1, Math.round(dimensions.width * scale));
  const height = Math.max(1, Math.round(dimensions.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.drawImage(img, 0, 0, width, height);

  const preferredType = "image/jpeg";
  let compressedBlob = await canvasToBlob(canvas, preferredType, quality);
  if (!compressedBlob) {
    compressedBlob = await canvasToBlob(canvas, "image/webp", quality);
  }

  if (!compressedBlob || compressedBlob.size >= file.size) {
    return null;
  }

  const extension = compressedBlob.type === "image/webp" ? "webp" : "jpg";
  const baseName = file.name.replace(/\.[^.]+$/, "");

  return new File([compressedBlob], `${baseName}.${extension}`, {
    type: compressedBlob.type,
    lastModified: file.lastModified,
  });
};

const hydratePhotoReadUrls = async <T extends PhotoRecord>(rows: T[]): Promise<T[]> => {
  return Promise.all(rows.map(async (row) => ({
    ...row,
    file_url: await mediaService.resolveDisplayUrl(row),
    thumbnail_url: row.thumbnail_url,
  } as T)));
};

export const photoService = {
  /**
   * Fetch approved photos for an event with optional pagination.
   */
  async fetchApproved(eventId: string, opts?: { limit?: number; offset?: number }): Promise<PhotoRecord[]> {
    const limit = opts?.limit ?? 1000;
    const offset = opts?.offset ?? 0;
    const query = supabase
      .from("photos")
      .select("id, file_url, file_path, created_at, uploaded_by, media_type, thumbnail_url, duration_seconds, original_file_path, storage_provider, storage_key, storage_url_mode, caption")
      .eq("event_id", eventId)
      .eq("approved", true)
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1);
    const { data, error } = await query;
    if (error) throw error;
    return hydratePhotoReadUrls((data || []) as PhotoRecord[]);
  },

  /**
   * Fetch ALL photos for an event (admin view).
   */
  async fetchAll(eventId: string): Promise<PhotoRecordFull[]> {
    const { data, error } = await supabase
      .from("photos")
      .select("id, file_url, file_path, created_at, uploaded_by, approved, event_id, media_type, thumbnail_url, duration_seconds, original_file_path, storage_provider, storage_key, storage_url_mode, google_drive_file_id, caption")
      .eq("event_id", eventId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return hydratePhotoReadUrls((data || []) as PhotoRecordFull[]);
  },

  /**
   * Fetch photo IDs uploaded by a specific device code for an event.
   */
  async fetchIdsByUploader(deviceCode: string, eventId?: string): Promise<string[]> {
    let query = supabase.from("photos").select("id").eq("uploaded_by", deviceCode);
    if (eventId) query = query.eq("event_id", eventId);
    const { data } = await query;
    return data ? data.map((p) => p.id) : [];
  },

  /**
   * Count photos by uploader.
   */
  async countByUploader(deviceCode: string): Promise<number> {
    const { count } = await supabase
      .from("photos")
      .select("*", { count: "exact", head: true })
      .eq("uploaded_by", deviceCode);
    return count || 0;
  },

  /**
   * Count all photos for an event.
   */
  async countByEvent(eventId: string): Promise<number> {
    const { count } = await supabase
      .from("photos")
      .select("*", { count: "exact", head: true })
      .eq("event_id", eventId);
    return count || 0;
  },

  /**
   * Check if upload is allowed based on event plan limits.
   * Returns { allowed, currentCount, limit }
   */
  async checkUploadAllowed(eventId: string): Promise<{ allowed: boolean; currentCount: number; limit: number | "unlimited" }> {
    const { data: plan } = await supabase
      .from("event_plans")
      .select("photo_limit, is_unlimited_photos")
      .eq("event_id", eventId)
      .maybeSingle();

    const limit = plan?.is_unlimited_photos ? "unlimited" as const : (plan?.photo_limit ?? 50);
    if (limit === "unlimited") return { allowed: true, currentCount: 0, limit };

    const currentCount = await this.countByEvent(eventId);
    return { allowed: currentCount < limit, currentCount, limit };
  },

  /**
   * Transfer photos from one device code to another.
   */
  async transferPhotos(fromCode: string, toCode: string): Promise<void> {
    const { error } = await supabase
      .from("photos")
      .update({ uploaded_by: toCode })
      .eq("uploaded_by", fromCode);
    if (error) throw error;
  },

  /**
   * Upload media file (photo/video) and create the DB record.
   * Returns the new media record or null on failure.
   * Validates against event plan limits before uploading.
   */
  async uploadPhoto(
    file: File,
    eventSlug: string,
    eventId: string,
    deviceCode: string,
    caption?: string | null
  ): Promise<{ id: string; file_url: string; file_path: string; media_type: "image" | "video"; thumbnail_url?: string | null; duration_seconds?: number | null; original_file_path?: string | null; caption?: string | null } | null> {
    // Check upload limit
    const { allowed, currentCount, limit } = await this.checkUploadAllowed(eventId);
    if (!allowed) {
      throw new Error(`Dosegnut je limit od ${limit} fotografija (trenutno: ${currentCount}). Nadogradite plan za više.`);
    }

    // Check if uploads are locked for this event
    const { data: lockCheck } = await supabase
      .from("events").select("uploads_locked").eq("id", eventId).single();
    if (lockCheck?.uploads_locked) {
      throw new Error("Uploadanje je zaključano za ovaj event.");
    }

    const mediaType = resolveMediaType(file.type);
    const featureFlags = await loadFeatureFlags();

    // Compute source hash for deduplication semantics.
    const originalFileHash = await computeFileHash(file);

    // Check if this exact file was already uploaded by this user for this event
    const { data: existingByOriginalHash } = await supabase
      .from("photos")
      .select("id, file_url, file_path, media_type, thumbnail_url, duration_seconds, original_file_path")
      .eq("event_id", eventId)
      .eq("uploaded_by", deviceCode)
      .eq("original_file_hash", originalFileHash)
      .maybeSingle();

    const { data: existingByLegacyHash } = existingByOriginalHash
      ? { data: null }
      : await supabase
        .from("photos")
        .select("id, file_url, file_path, media_type, thumbnail_url, duration_seconds, original_file_path")
        .eq("event_id", eventId)
        .eq("uploaded_by", deviceCode)
        .eq("file_hash", originalFileHash)
        .maybeSingle();

    const existing = existingByOriginalHash ?? existingByLegacyHash;

    if (existing) {
      // Already uploaded — return existing record instead of re-uploading
      return {
        id: existing.id,
        file_url: existing.file_url,
        file_path: existing.file_path,
        media_type: existing.media_type as "image" | "video",
        thumbnail_url: existing.thumbnail_url,
        duration_seconds: existing.duration_seconds,
        original_file_path: existing.original_file_path,
      };
    }

    let uploadFile = file;
    if (mediaType === "image") {
      const compressionQuality = Number.isFinite(featureFlags.image_upload_compression_quality)
        ? Math.min(1, Math.max(0.5, featureFlags.image_upload_compression_quality))
        : DEFAULT_IMAGE_UPLOAD_COMPRESSION_QUALITY;
      const compressionMinBytes = (Number.isFinite(featureFlags.image_upload_compression_max_size_kb) && featureFlags.image_upload_compression_max_size_kb >= 100)
        ? featureFlags.image_upload_compression_max_size_kb * 1024
        : DEFAULT_IMAGE_UPLOAD_COMPRESSION_MIN_BYTES;
      const compressedFile = await compressImageForUpload(file, compressionQuality, compressionMinBytes);
      if (compressedFile) {
        uploadFile = compressedFile;
      }
    }

    const storedFileHash = await computeFileHash(uploadFile);
    const _now = new Date();
    const mediaId = [
      _now.getFullYear(),
      String(_now.getMonth() + 1).padStart(2, "0"),
      String(_now.getDate()).padStart(2, "0"),
    ].join("") + "_" + [
      String(_now.getHours()).padStart(2, "0"),
      String(_now.getMinutes()).padStart(2, "0"),
      String(_now.getSeconds()).padStart(2, "0"),
      String(_now.getMilliseconds()).padStart(3, "0"),
    ].join("");
    const { filePath, fileUrl, storageMetadata } = await mediaService.uploadEventMedia({
      eventSlug,
      deviceCode,
      mediaId,
      file: uploadFile,
      kind: "photo",
      contentType: uploadFile.type || file.type || "image/jpeg",
      cacheControl: "3600",
    });

    let thumbnailUrl: string | null = null;
    let durationSeconds: number | null = null;
    const thumbnailPaths: string[] = [];
    let originalFilePath: string | null = null;

    if (mediaType === "image" && uploadFile !== file) {
      const originalExtension = file.name.split(".").pop() || "jpg";
      const originalPath = `events/${eventSlug}/originals-source/${deviceCode}/${mediaId}.${originalExtension}`;
      const originalUpload = await mediaService.uploadEventMedia({
        eventSlug,
        deviceCode,
        mediaId,
        file,
        kind: "photo",
        customPath: originalPath,
        contentType: file.type || "image/jpeg",
        cacheControl: "3600",
      });
      originalFilePath = originalUpload.filePath;
    }

    if (mediaType === "video") {
      durationSeconds = await getVideoDuration(file);
      const thumbnailBlob = await createVideoThumbnail(file);
      if (thumbnailBlob) {
        const thumbnailPath = mediaService.buildThumbnailPath({ eventSlug, mediaId });
        const thumbnailFile = new File([thumbnailBlob], `${mediaId}-thumb.jpg`, { type: "image/jpeg" });
        const thumbnailUpload = await mediaService.uploadEventMedia({
          eventSlug,
          deviceCode,
          mediaId,
          file: thumbnailFile,
          kind: "thumbnail",
          customPath: thumbnailPath,
          contentType: "image/jpeg",
          cacheControl: "3600",
        });
        thumbnailUrl = thumbnailUpload.fileUrl;
      }
    } else {
      const variants = await createImageThumbnailVariants(uploadFile);
      for (const variant of variants) {
        const thumbnailPath = `events/${eventSlug}/thumbs/${mediaId}-w${variant.width}.jpg`;
        const thumbnailFile = new File([variant.blob], `${mediaId}-w${variant.width}.jpg`, { type: "image/jpeg" });
        const thumbnailUpload = await mediaService.uploadEventMedia({
          eventSlug,
          deviceCode,
          mediaId,
          file: thumbnailFile,
          kind: "thumbnail",
          customPath: thumbnailPath,
          contentType: "image/jpeg",
          cacheControl: "3600",
        });
        thumbnailPaths.push(thumbnailUpload.filePath);
        if (variant.width === DEFAULT_THUMBNAIL_VARIANT_WIDTH) {
          thumbnailUrl = thumbnailUpload.fileUrl;
        }
      }
    }

    const { data, error } = await supabase
      .from("photos")
      .insert({
        file_path: filePath,
        file_url: fileUrl,
        uploaded_by: deviceCode,
        event_id: eventId,
        media_type: mediaType,
        thumbnail_url: thumbnailUrl,
        duration_seconds: durationSeconds,
        file_hash: originalFileHash,
        original_file_hash: originalFileHash,
        stored_file_hash: storedFileHash,
        file_size: uploadFile.size,
        original_file_size: file.size,
        stored_file_size: uploadFile.size,
        original_file_path: originalFilePath,
        thumbnail_paths: thumbnailPaths,
        ...(caption ? { caption } : {}),
        ...storageMetadata,
      } as any)
      .select("id")
      .single();

    if (error) throw error;
    if (mediaType === "video") trackVideoUpload(eventSlug); else trackPhotoUpload(eventSlug);
    return {
      id: data.id,
      file_url: fileUrl,
      file_path: filePath,
      media_type: mediaType,
      thumbnail_url: thumbnailUrl,
      duration_seconds: durationSeconds,
      original_file_path: originalFilePath,
      caption: caption ?? null,
    };
  },

  /**
   * Delete a photo (storage + DB).
   */
  async deletePhoto(photoId: string, filePath: string): Promise<void> {
    const { data: row } = await supabase
      .from("photos")
      .select("original_file_path, thumbnail_paths")
      .eq("id", photoId)
      .maybeSingle();

    const extraPaths = [
      row?.original_file_path,
      ...(Array.isArray(row?.thumbnail_paths) ? row.thumbnail_paths : []),
    ].filter((path): path is string => Boolean(path));
    const allPaths = Array.from(new Set([filePath, ...extraPaths]));

    await mediaService.deleteEventMedia(allPaths);
    const { error } = await supabase.from("photos").delete().eq("id", photoId);
    if (error) throw error;
  },

  /**
   * Approve a photo.
   */
  async approve(photoId: string): Promise<void> {
    const { error } = await supabase
      .from("photos")
      .update({ approved: true })
      .eq("id", photoId);
    if (error) throw error;
  },

  /**
   * Unapprove a photo (send back to moderation).
   */
  async unapprove(photoId: string): Promise<void> {
    const { error } = await supabase
      .from("photos")
      .update({ approved: false })
      .eq("id", photoId);
    if (error) throw error;
  },

  /**
   * Fetch event_id list for all photos (for admin stats).
   */
  async fetchEventIds(): Promise<string[]> {
    const { data } = await supabase.from("photos").select("event_id");
    return data ? data.map((p) => p.event_id) : [];
  },
};
